import React, {useEffect, useState} from "react";
import {getQueryParam} from "../../../utility/navigationUtil";
import CCOComponentWrapper from "../../fundamentals/CCOComponentWrapper";
import CCOHeadline from "../../fundamentals/CCOHeadline";
import {Grid, Typography} from "@mui/material";
import CCOContainer from "../../fundamentals/CCOContainer";
import {useQuery} from "@apollo/client";
import GET_SEARCH_HIT_LIST from "../../../queries/GeSearchHitList";
import Button from "@mui/material/Button";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {useTranslation} from "react-i18next";
import {CmTeasable} from "../../../../packages/graphql-layer/src";
import C036SearchField from "./C036SearchField";
import {usePageContext} from "../../../context/PageContext";
import C018StandardTeaser from "../C018StandardTeaser/C018StandardTeaser";
import CCOLoadingSpinner from "../../fundamentals/CCOLoadingSpinner";
import C036SearchFilter from "./C036SearchFilter";

const query = GET_SEARCH_HIT_LIST;

const FALLBACK_DOC_TYPES = ['CMChannel', 'SOSPublication', 'SOSNews', 'SOSPressrelease', 'SOSSpecialTopic'];
const ALWAYS_INCLUDED = ['CMChannel'];
const HIT_LIST_LIMIT = 10;

const getDocTypesForQuery = (
    filterValues: Record<string, { value: string; isActive: boolean }>,
    docTypesFromFilters: string[],
    fallbackDocTypes: string[],
    alwaysIncluded: string[]
): string[] => {
    const activeFilters = Object.entries(filterValues)
        .filter(([, { isActive }]) => isActive)
        .map(([, { value }]) => value);

    if (activeFilters.length > 0) {
        return Array.from(new Set(activeFilters));
    }

    // When no filters are active, combine filter doctypes and always-included doctypes
    const combinedDocTypes = new Set([
        ...(docTypesFromFilters.length > 0 ? docTypesFromFilters : fallbackDocTypes),
        ...alwaysIncluded
    ]);

    return Array.from(combinedDocTypes);
};


function C036SearchAction() {
    const pageContext = usePageContext();
    const filterSettings = pageContext?.page?.searchSettings?.["sos.doctype.filter"];
    const showFilterSetting = pageContext?.page?.searchSettings?.["sos.search.showFilter"];
    const sid = pageContext.siteId;
    const {t} = useTranslation()
    const initalFilterValues = Object.fromEntries(Object.keys(filterSettings).map(
        key => [key, {value: filterSettings[key], isActive: false}]
    ));
    const [filterValues, setFilterValues] = useState(initalFilterValues);
    const [search, setSearch] = useState("")
    const [concat, setConcat] = useState(false)
    const [offset, setOffset] = useState<number>(0);
    const [hits, setHits] = useState([]);

    const docTypesFromFilters = initalFilterValues
        ? Object.values(initalFilterValues).map(item => item.value)
        : [];
    const showFilter = showFilterSetting && docTypesFromFilters && docTypesFromFilters.length > 0;

    const docTypesForQuery = getDocTypesForQuery(
        filterValues,
        docTypesFromFilters,
        FALLBACK_DOC_TYPES,
        ALWAYS_INCLUDED
    );

    useEffect(() => {
        if (typeof window !== "undefined") {
            const searchString = window.location.search;
            const fieldValue = getQueryParam(searchString, "search");
            if (fieldValue) {
                setSearch(fieldValue)
            }
        }
    }, []);

    useEffect(() => {
        loadNew(search);
        // eslint-disable-next-line
    }, [filterValues]);


    const {data, loading} = useQuery(query, {
        variables: {search: search, offset: offset, limit: HIT_LIST_LIMIT, siteId: sid, docTypes: docTypesForQuery},
        onError: (err) => {
            console.log("Can't load search hits:")
            console.log(err)
        },
        onCompleted: (data) => {
            concat ? setHits(hits.concat(data?.content.search.result)) : setHits(data?.content.search.result);
            setConcat(true);
        }, fetchPolicy: "network-only"
    })

    const loadMore = () => {
        setOffset((offset) => offset + HIT_LIST_LIMIT)
    };

    const loadNew = (input?: string) => {
        if (input) {
            setConcat(false)
            setOffset(0)
            setSearch(input)
        }
    }

    const handleFilterChange = (key: string) => {
        setFilterValues((prevState) => ({
            ...prevState,
            [key]: {
                ...prevState[key],
                isActive: !prevState[key].isActive,
            },
        }));
    };

    const handleResetFilters = () => {
        setFilterValues(initalFilterValues)
    };

    const searchHits = hits.map((hit: any, index: number) => {
        const teaser = hit as CmTeasable
        return (
            <C018StandardTeaser dataCypress={'searchResult'} key={"h" + index} cmTeaser={teaser}/>
        );
    });

    const totalNumberResults = data?.content?.search?.numFound ?? null;
    const subline = t("search.hit_first_part") + search + t("search.hit_second_part") + totalNumberResults
        + t("search.hit_third_part") + (totalNumberResults > 0 ? ":" : "");

    return <>
        <CCOComponentWrapper background={"grey"} smallmargin={true} noTopMargin={true}>
            <CCOHeadline id="suchcontainer" headline={t("search.result_label")}/>
            <CCOContainer width="medium">
                <Grid container
                      direction="column"
                      marginY={6}
                      alignItems="center"
                      justifyContent="center"
                >
                    <Grid item key="searchfield">
                        <C036SearchField dataCypress='resultSearchField' val={search} isInContent={true}
                                         reloadFunction={loadNew}/>
                    </Grid>
                </Grid>
            </CCOContainer>
            {showFilter ? (
                <C036SearchFilter
                    filterValues={filterValues}
                    handleFilterChange={handleFilterChange}
                    handleResetFilters={handleResetFilters}
                />
            ) : <></>}
        </CCOComponentWrapper>


        <CCOComponentWrapper>
            <CCOContainer width="medium">
                <Grid container>
                    <Grid item xs={12} key="result">
                        <Typography variant="h3" align="center"
                                    marginBottom={6}>{!loading ? subline : null}</Typography>
                    </Grid>
                </Grid>
            </CCOContainer>
            <CCOContainer dataCypress={'searchResultContainer'} width="medium">
                {!loading ? searchHits :
                    <CCOContainer width="medium">
                        <CCOLoadingSpinner/>
                    </CCOContainer>
                }
            </CCOContainer>
            {hits.length > 0 && totalNumberResults - hits.length > 0 ? <CCOContainer width="s">
                <Grid container
                      direction="column"
                      marginY={6}
                      alignItems="center"
                      justifyContent="center"
                >
                    <Grid item>
                        <Button key="btn" variant="contained" startIcon={<ExpandMoreIcon/>}
                                onClick={loadMore}>{t("coo_show_more")}</Button>
                    </Grid>
                </Grid>
            </CCOContainer> : <></>}
        </CCOComponentWrapper>
    </>
}

export default C036SearchAction;
